import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCheck, CheckCircle2, Sparkles, Zap, ThumbsUp, ThumbsDown, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_OPTIONS = [
  { verdict: "ABSOLUTELY", label: "Absolutely", sub: "Top of my list to rehire", Icon: ThumbsUp, tone: "bg-teal/10 text-teal border-teal/40 hover:bg-teal/20" },
  { verdict: "YES", label: "Yes", sub: "I'd rehire them", Icon: ThumbsUp, tone: "bg-pink/10 text-pink border-pink/40 hover:bg-pink/20" },
  { verdict: "MAYBE", label: "Maybe", sub: "It depends on the role", Icon: HelpCircle, tone: "bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200" },
  { verdict: "NO", label: "No", sub: "I would not rehire", Icon: ThumbsDown, tone: "bg-red-100 text-red-700 border-red-300 hover:bg-red-200" },
];

export default function ReferenceFormPage() {
  const { token } = useParams();
  const [ref, setRef] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [oneTapMode, setOneTapMode] = useState(false);
  const [oneTapVerdict, setOneTapVerdict] = useState(null);
  const [oneTapComment, setOneTapComment] = useState("");

  useEffect(() => {
    axios.get(`${API}/reference/${token}`)
      .then(r => { setRef(r.data); if (r.data.status === "SUBMITTED") setDone(true); })
      .catch(e => setError(e.response?.data?.detail || "Link is invalid or expired."));
  }, [token]);

  const submit = async () => {
    const required = ref.questions.filter(q => !answers[q.id]);
    if (required.length > 0) { toast.error(`Please answer all ${ref.questions.length} questions`); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reference/${token}`, { answers });
      setDone(true);
      toast.success("Thank you — your reference has been submitted.");
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  const submitOneTap = async () => {
    if (!oneTapVerdict) { toast.error("Please pick one option"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reference/${token}/quick-approve`, {
        verdict: oneTapVerdict,
        comment: oneTapComment.trim() || null,
      });
      setDone(true);
      toast.success("Thank you — your one-tap recommendation is in.");
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-neutral-50 grid place-items-center p-6">
      <Card className="max-w-md w-full border-0 shadow-md">
        <CardContent className="p-8 text-center">
          <div className="text-red-500 font-semibold mb-2">Unavailable</div>
          <p className="text-neutral-600 text-sm">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (!ref) return <div className="min-h-screen grid place-items-center text-neutral-500">Loading…</div>;

  if (done) return (
    <div className="min-h-screen bg-neutral-50 grid place-items-center p-6">
      <Card className="max-w-md w-full border-0 shadow-md" data-testid="ref-thank-you">
        <CardContent className="p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-teal/10 text-teal grid place-items-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="text-xl font-bold font-display">Thank you</h1>
          <p className="text-neutral-600 text-sm mt-2">
            Your reference for <span className="font-semibold">{ref.candidate_name}</span> has been submitted.
            Alfred is now summarising your answers for the hiring team.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50 py-10 px-4" data-testid="reference-form-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border text-xs uppercase tracking-widest text-neutral-500">
            <UserCheck className="w-3.5 h-3.5 text-pink" /> Reference check
          </div>
          <h1 className="text-3xl font-bold font-display mt-4">Reference for {ref.candidate_name}</h1>
          <p className="text-neutral-600 text-sm mt-2">
            Hi {ref.referee_name.split(" ")[0]} — thanks for taking a few minutes. Your candid answers help us give
            {" "}<span className="font-semibold">{ref.candidate_name}</span> a fair, well-rounded evaluation.
            <br /><span className="text-xs text-neutral-500">Your responses are confidential and summarised by AI for the hiring team.</span>
          </p>
        </div>

        {/* Smart One-Tap Approval Panel */}
        <Card className="border-2 border-dashed border-pink/40 bg-gradient-to-br from-pink/5 to-transparent" data-testid="ref-one-tap-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-pink" />
                  <h3 className="font-display font-bold text-sm uppercase tracking-widest text-pink">Short on time?</h3>
                </div>
                <p className="text-sm text-neutral-700 mt-1">
                  Give a <strong>10-second one-tap recommendation</strong> instead of filling the full form. Alfred will summarise it for the hiring team.
                </p>
              </div>
              <Button
                variant={oneTapMode ? "default" : "outline"}
                size="sm"
                onClick={() => setOneTapMode(v => !v)}
                data-testid="ref-one-tap-toggle"
                className={oneTapMode ? "btn-primary" : ""}
              >
                {oneTapMode ? "Use full form instead" : "Give a one-tap recommendation"}
              </Button>
            </div>

            {oneTapMode && (
              <div className="mt-5 space-y-4" data-testid="ref-one-tap-panel">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {QUICK_OPTIONS.map(o => (
                    <button
                      key={o.verdict}
                      type="button"
                      onClick={() => setOneTapVerdict(o.verdict)}
                      data-testid={`ref-one-tap-${o.verdict.toLowerCase()}`}
                      className={`rounded-xl border-2 p-3 text-left transition-all ${o.tone} ${oneTapVerdict === o.verdict ? "ring-2 ring-offset-2 ring-pink shadow" : ""}`}
                    >
                      <o.Icon className="w-4 h-4 mb-1" />
                      <div className="font-bold text-sm">{o.label}</div>
                      <div className="text-[10px] opacity-80 leading-tight mt-0.5">{o.sub}</div>
                    </button>
                  ))}
                </div>
                <div>
                  <Label className="text-xs text-neutral-600">Optional 1-sentence comment</Label>
                  <Textarea
                    rows={2}
                    value={oneTapComment}
                    onChange={(e) => setOneTapComment(e.target.value.slice(0, 500))}
                    className="mt-1"
                    placeholder="Anything specific worth flagging? (optional)"
                    data-testid="ref-one-tap-comment"
                  />
                  <div className="text-[10px] text-neutral-400 text-right mt-1">{oneTapComment.length}/500</div>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={submitOneTap}
                    disabled={submitting || !oneTapVerdict}
                    className="btn-primary rounded-full px-6"
                    data-testid="ref-one-tap-submit"
                  >
                    <Zap className="w-4 h-4 mr-2" /> {submitting ? "Submitting…" : "Submit one-tap recommendation"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {!oneTapMode && (
          <>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 space-y-5">
                {ref.questions.map((q, i) => (
                  <div key={q.id} data-testid={`ref-q-${q.id}`}>
                    <Label className="text-sm">
                      <span className="text-pink font-mono text-xs mr-2">{String(i + 1).padStart(2, "0")}</span>
                      {q.label}
                    </Label>
                    {q.type === "select" ? (
                      <Select value={answers[q.id] || ""} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v })}>
                        <SelectTrigger className="mt-2" data-testid={`ref-input-${q.id}`}><SelectValue placeholder="Choose one…" /></SelectTrigger>
                        <SelectContent>
                          {q.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Textarea rows={3} value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                        className="mt-2" placeholder="Share honest, specific detail — 2-3 sentences is perfect."
                        data-testid={`ref-input-${q.id}`} />
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex justify-center">
              <Button onClick={submit} disabled={submitting} className="btn-primary rounded-full px-8" data-testid="ref-submit-btn">
                <Sparkles className="w-4 h-4 mr-2" /> {submitting ? "Submitting…" : "Submit reference"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
